import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from '@mui/material';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { getHousekeepingStaff, getRoomCleaning, completeRoomCleaning, getHousekeepingSettings, updateRoomCleaning } from '../../services/api';
import HousekeepingCleaningChecklist, {
  alignCheckedIdsToItems,
  areRequiredChecklistItemsChecked,
  checklistStateFromCleaning,
  cleaningStatusFromChecklist,
  cleaningStatusLabel,
  normalizeChecklistItems,
} from '../housekeeping/HousekeepingCleaningChecklist';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../../utils/drawerOverlayProps';
import {
  getHousekeepingDrawerFooterSx,
  getHousekeepingDrawerHeaderSx,
  getHousekeepingThemeColors,
} from '../../utils/housekeepingMobileLayout';

const THEME_COLOR = '#4ce1b6';

const DURATION_OPTIONS = [10, 15, 20, 30, 45, 60];

const CLEANING_TYPE_LABELS = {
  STAYOVER: 'Stay Over',
  CHECKOUT: 'Check Out',
  DEEP: 'Deep Clean',
};

const fieldSx = {
  '& .MuiInputBase-root': {
    minHeight: 48,
  },
};

function formatCleaningType(value) {
  return CLEANING_TYPE_LABELS[value] || value || 'Room cleaning';
}

function parseTimeToMoment(value) {
  if (!value) return null;
  if (moment.isMoment(value)) return value.isValid() ? value : null;
  const parsed = moment(value, ['HH:mm:ss', 'HH:mm', 'h:mm A', 'hh:mm A'], true);
  return parsed.isValid() ? parsed : null;
}

function formatTimeForApi(value) {
  if (!value) return null;
  if (moment.isMoment(value) && value.isValid()) return value.format('HH:mm:ss');
  if (typeof value === 'string') return value;
  return null;
}

function defaultStartTime() {
  return moment().startOf('day').hour(10).minute(0).second(0).millisecond(0);
}

const HK_CACHE_TTL_MS = 60 * 1000;
const hkStaffCache = { at: 0, data: null, inflight: null };
const hkSettingsCache = { at: 0, data: null, inflight: null };

function cacheIsFresh(entry) {
  return Boolean(entry?.data) && Date.now() - entry.at < HK_CACHE_TTL_MS;
}

async function loadHousekeepingStaffCached() {
  if (cacheIsFresh(hkStaffCache)) return hkStaffCache.data;
  if (hkStaffCache.inflight) return hkStaffCache.inflight;
  hkStaffCache.inflight = getHousekeepingStaff({ page_size: 100 })
    .catch(() => ({ results: [] }))
    .then((data) => {
      hkStaffCache.at = Date.now();
      hkStaffCache.data = data;
      hkStaffCache.inflight = null;
      return data;
    });
  return hkStaffCache.inflight;
}

async function loadHousekeepingSettingsCached() {
  if (cacheIsFresh(hkSettingsCache)) return hkSettingsCache.data;
  if (hkSettingsCache.inflight) return hkSettingsCache.inflight;
  hkSettingsCache.inflight = getHousekeepingSettings()
    .catch(() => null)
    .then((data) => {
      hkSettingsCache.at = Date.now();
      hkSettingsCache.data = data;
      hkSettingsCache.inflight = null;
      return data;
    });
  return hkSettingsCache.inflight;
}

function parseHotelChecklist(settings) {
  const items = normalizeChecklistItems(settings?.cleaning_checklist, {
    defaultMandatory: Boolean(settings?.checklist_mandatory),
  });
  return {
    enabled: Boolean(settings?.checklist_enabled) && items.length > 0,
    items,
  };
}

function CleaningChecklistSkeleton({ isDark = false }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Skeleton variant="rounded" width={140} height={18} animation="wave" />
        <Box sx={{ flex: 1 }} />
        <Skeleton variant="rounded" width={72} height={22} animation="wave" />
        <Skeleton variant="rounded" width={88} height={22} animation="wave" />
      </Box>
      {Array.from({ length: 6 }).map((_, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.85 }}>
          <Skeleton variant="rounded" width={20} height={20} animation="wave" />
          <Skeleton variant="rounded" width={`${70 - (index % 3) * 8}%`} height={16} animation="wave" />
          <Box sx={{ flex: 1 }} />
          <Skeleton variant="rounded" width={36} height={18} animation="wave" />
        </Box>
      ))}
    </Box>
  );
}

export function prefetchHousekeepingDrawerData() {
  loadHousekeepingStaffCached();
  loadHousekeepingSettingsCached();
}

export default function DashboardHousekeepingDrawer({
  cleaningId,
  open,
  onClose,
  onSuccess,
  onError,
  themeMode = 'dark',
}) {
  const isDark = themeMode === 'dark';
  const navigate = useNavigate();
  const themeColors = getHousekeepingThemeColors(isDark);
  const fieldSurface = isDark ? themeColors.surface : '#ffffff';
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, fieldSurface),
    [isDark, fieldSurface]
  );
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const loadGenRef = useRef(0);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [cleaning, setCleaning] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [staffList, setStaffList] = useState(() => hkStaffCache.data?.results || []);
  const [staffReady, setStaffReady] = useState(() => Boolean(hkStaffCache.data));
  const hasStaff = staffList.length > 0;
  const [staffId, setStaffId] = useState('');
  const [startTime, setStartTime] = useState(defaultStartTime);
  const [durationMinutes, setDurationMinutes] = useState(15);
  const [checklistEnabled, setChecklistEnabled] = useState(() => parseHotelChecklist(hkSettingsCache.data).enabled);
  const [checklistItems, setChecklistItems] = useState(() => {
    const parsed = parseHotelChecklist(hkSettingsCache.data);
    return parsed.enabled ? parsed.items : [];
  });
  const [settingsReady, setSettingsReady] = useState(() => cacheIsFresh(hkSettingsCache));
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const cleaningRef = useRef(null);

  const applyHotelChecklist = useCallback((settings, payload = null) => {
    const parsed = parseHotelChecklist(settings);
    setChecklistEnabled(parsed.enabled);
    setChecklistItems(parsed.enabled ? parsed.items : []);
    setSettingsReady(true);
    if (!parsed.enabled) {
      setCheckedIds(new Set());
      return;
    }
    const source = payload || cleaningRef.current;
    if (!source) return;
    const saved = checklistStateFromCleaning(source);
    setCheckedIds(alignCheckedIdsToItems(parsed.items, saved.checkedIds, saved.items));
  }, []);

  const endTime = useMemo(() => {
    if (!startTime || !moment.isMoment(startTime) || !startTime.isValid()) return null;
    return startTime.clone().add(durationMinutes, 'minutes');
  }, [startTime, durationMinutes]);

  const resetForm = useCallback(() => {
    setRemarks('');
    setStaffId('');
    setStartTime(defaultStartTime());
    setDurationMinutes(15);
    setCheckedIds(new Set());
    setFieldErrors({});
    setError('');
  }, []);

  const applyCleaningToForm = useCallback((payload, staffOptions = [], checkedIdsOverride = null) => {
    setRemarks(payload?.remarks || '');

    const staffRef = payload?.staff;
    const resolvedStaffId =
      typeof staffRef === 'object' && staffRef != null
        ? staffRef.id
        : (payload?.staff_id ?? staffRef ?? '');
    const hasAssignedStaff = resolvedStaffId !== '' && resolvedStaffId != null;
    const fallbackStaffId = staffOptions[0]?.id;
    setStaffId(
      hasAssignedStaff
        ? (Number(resolvedStaffId) || resolvedStaffId)
        : (fallbackStaffId ?? '')
    );

    const startM = parseTimeToMoment(payload?.start_time);
    const endM = parseTimeToMoment(payload?.end_time);
    let duration = 15;
    if (startM && endM && endM.isAfter(startM)) {
      duration = endM.diff(startM, 'minutes');
      if (!DURATION_OPTIONS.includes(duration)) {
        const nearest = DURATION_OPTIONS.reduce((best, option) => (
          Math.abs(option - duration) < Math.abs(best - duration) ? option : best
        ), DURATION_OPTIONS[0]);
        duration = nearest;
      }
    }
    setStartTime(startM || defaultStartTime());
    setDurationMinutes(duration);
    if (checkedIdsOverride) {
      setCheckedIds(checkedIdsOverride);
      return;
    }
    const saved = checklistStateFromCleaning(payload);
    setCheckedIds(saved.checkedIds);
  }, []);

  const loadDrawerData = useCallback(async () => {
    if (!cleaningId) return;
    const gen = loadGenRef.current + 1;
    loadGenRef.current = gen;
    setLoading(true);
    setError('');
    setFieldErrors({});
    const cachedStaff = hkStaffCache.data?.results || [];
    if (cachedStaff.length) {
      setStaffList(cachedStaff);
      setStaffReady(true);
    } else {
      setStaffReady(false);
    }
    if (hkSettingsCache.data) {
      applyHotelChecklist(hkSettingsCache.data);
    } else {
      setSettingsReady(false);
    }
    try {
      const staffPromise = loadHousekeepingStaffCached();
      const settingsPromise = loadHousekeepingSettingsCached();
      const payload = await getRoomCleaning(cleaningId);
      if (gen !== loadGenRef.current) return;
      cleaningRef.current = payload;
      setCleaning(payload);
      applyCleaningToForm(payload, cachedStaff);
      if (cacheIsFresh(hkSettingsCache)) {
        applyHotelChecklist(hkSettingsCache.data, payload);
      }
      setLoading(false);

      const [staffResponse, settings] = await Promise.all([staffPromise, settingsPromise]);
      if (gen !== loadGenRef.current) return;
      const staffOptions = staffResponse?.results || [];
      setStaffList(staffOptions);
      setStaffReady(true);
      applyCleaningToForm(payload, staffOptions);
      applyHotelChecklist(settings, payload);
    } catch (err) {
      if (gen !== loadGenRef.current) return;
      setCleaning(null);
      cleaningRef.current = null;
      setError(err?.error || err?.detail || 'Failed to load room cleaning task.');
      setLoading(false);
      setSettingsReady(Boolean(hkSettingsCache.data));
    }
  }, [cleaningId, applyCleaningToForm, applyHotelChecklist]);

  useEffect(() => {
    if (open) {
      prefetchHousekeepingDrawerData();
      if (hkStaffCache.data?.results?.length) {
        setStaffList(hkStaffCache.data.results);
        setStaffReady(true);
      }
      if (hkSettingsCache.data) {
        applyHotelChecklist(hkSettingsCache.data);
      }
    }
    if (open && cleaningId) {
      loadDrawerData();
    } else if (!open) {
      setCleaning(null);
      resetForm();
    }
  }, [open, cleaningId, loadDrawerData, resetForm, applyHotelChecklist]);

  const roomLabel = useMemo(() => {
    if (!cleaning) return 'Room';
    if (cleaning.room_number) return `Room ${cleaning.room_number}`;
    return 'Room';
  }, [cleaning]);

  const checklistComplete = !checklistEnabled || areRequiredChecklistItemsChecked(checklistItems, checkedIds);
  const derivedCleaningStatus = cleaningStatusFromChecklist({
    checklistEnabled,
    items: checklistItems,
    checkedIds,
  }) || String(cleaning?.status || 'PENDING').toUpperCase();
  const derivedStatusChipSx = {
    PENDING: { bgcolor: 'rgba(255,183,77,0.16)', color: '#ffb74d' },
    IN_PROGRESS: { bgcolor: 'rgba(41,182,246,0.16)', color: '#29b6f6' },
    COMPLETED: { bgcolor: 'rgba(76,175,80,0.16)', color: '#66bb6a' },
  }[derivedCleaningStatus] || { bgcolor: 'rgba(255,183,77,0.16)', color: '#ffb74d' };

  const validateForm = ({ requireCompleteChecklist = false } = {}) => {
    const nextErrors = {};
    if (!staffId) nextErrors.staff = 'Select housekeeping staff';
    if (!startTime || !moment.isMoment(startTime) || !startTime.isValid()) {
      nextErrors.start_time = 'Select a start time';
    }
    if (!durationMinutes || durationMinutes < 1) {
      nextErrors.duration = 'Select duration';
    }
    if (
      requireCompleteChecklist
      && checklistEnabled
      && !areRequiredChecklistItemsChecked(checklistItems, checkedIds)
    ) {
      nextErrors.checklist = 'Check every required item before marking the room clean.';
    }
    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildProgressPayload = (nextCheckedIds, status) => {
    const roomId =
      typeof cleaning?.room === 'object' && cleaning.room != null
        ? cleaning.room.id
        : cleaning?.room;
    return {
      room: roomId,
      staff: staffId || null,
      cleaning_date: cleaning?.cleaning_date,
      start_time: formatTimeForApi(startTime),
      end_time: formatTimeForApi(endTime),
      cleaning_type: cleaning?.cleaning_type,
      status,
      remarks,
      checklist_item_ids: Array.from(nextCheckedIds),
    };
  };

  const canMarkClean = settingsReady && (!checklistEnabled || checklistComplete);
  const primaryActionLabel = canMarkClean ? 'Mark Clean' : 'Save Progress';

  const handlePrimaryAction = async () => {
    if (!cleaning?.id) return;
    const markClean = canMarkClean;
    if (!validateForm({ requireCompleteChecklist: markClean })) return;

    const roomId =
      typeof cleaning.room === 'object' && cleaning.room != null
        ? cleaning.room.id
        : cleaning.room;
    setSubmitting(true);
    setError('');
    try {
      if (markClean) {
        await completeRoomCleaning(cleaning.id, {
          staff: staffId,
          start_time: formatTimeForApi(startTime),
          end_time: formatTimeForApi(endTime),
          remarks,
          checklist_item_ids: Array.from(checkedIds),
        });
        onClose?.();
        onSuccess?.({ roomId, cleaningId: cleaning.id, status: 'COMPLETED' });
        return;
      }
      const derived = cleaningStatusFromChecklist({
        checklistEnabled,
        items: checklistItems,
        checkedIds,
      }) || 'PENDING';
      const status = derived === 'COMPLETED' ? 'IN_PROGRESS' : derived;
      const updated = await updateRoomCleaning(
        cleaning.id,
        buildProgressPayload(checkedIds, status),
      );
      setCleaning(updated);
      onClose?.();
      onSuccess?.({ roomId, cleaningId: cleaning.id, status });
    } catch (err) {
      const message = markClean
        ? (err?.checklist?.[0] || err?.error || err?.detail || 'Failed to mark room as clean.')
        : (err?.error || err?.detail || err?.staff?.[0] || 'Failed to save cleaning progress.');
      if (markClean) onError?.(message);
      else setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const durationToggleSx = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.75,
    '& .MuiToggleButtonGroup-grouped': {
      border: `1px solid ${themeColors.surfaceBorder} !important`,
      borderRadius: '999px !important',
      mx: 0,
      px: 1.4,
      py: 0.45,
      minHeight: 36,
      textTransform: 'none',
      fontWeight: 700,
      fontSize: '0.78rem',
      color: themeColors.subtleText,
      backgroundColor: isDark ? themeColors.chipBg : alpha('#000', 0.04),
      '&.Mui-selected': {
        backgroundColor: `${alpha(THEME_COLOR, isDark ? 0.22 : 0.16)} !important`,
        color: `${isDark ? '#ecfeff' : '#0f172a'} !important`,
        borderColor: `${alpha(THEME_COLOR, 0.5)} !important`,
      },
    },
  };

  const cleaningTimePanelSx = {
    p: 1.5,
    borderRadius: 2,
    border: `1px solid ${isDark ? themeColors.surfaceBorderStrong : alpha(THEME_COLOR, 0.2)}`,
    background: isDark
      ? `linear-gradient(165deg, ${themeColors.headerBg} 0%, #12151c 100%)`
      : alpha(THEME_COLOR, 0.06),
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 1.25,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            backgroundColor: isDark ? themeColors.surface : '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            ...getHousekeepingDrawerHeaderSx(isDark),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CleaningServicesIcon sx={{ color: THEME_COLOR, fontSize: 24 }} />
            <Typography variant="h6" sx={{ color: isDark ? themeColors.text : '#333', fontWeight: 600 }}>
              Room Cleaning
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: isDark ? '#999' : '#666' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: THEME_COLOR }} />
            </Box>
          ) : error && !cleaning ? (
            <Alert severity="error">{error}</Alert>
          ) : cleaning ? (
            <>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${themeColors.surfaceBorder}`,
                  background: isDark ? themeColors.surfaceGradient : '#fafcff',
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 0.5 }}>{roomLabel}</Typography>
                {cleaning.room_type_name ? (
                  <Typography sx={{ color: themeColors.subtleText, mb: 1.2 }}>
                    {cleaning.room_type_name}
                  </Typography>
                ) : null}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  <Chip
                    size="small"
                    label={formatCleaningType(cleaning.cleaning_type)}
                    sx={{ bgcolor: 'rgba(76,225,182,0.14)', color: THEME_COLOR, fontWeight: 700 }}
                  />
                  <Chip
                    size="small"
                    label={cleaningStatusLabel(derivedCleaningStatus)}
                    sx={{ ...derivedStatusChipSx, fontWeight: 700 }}
                  />
                  {cleaning.cleaning_date ? (
                    <Chip
                      size="small"
                      label={moment(cleaning.cleaning_date).format('DD MMM YYYY')}
                      sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)' }}
                    />
                  ) : null}
                </Box>
              </Box>

              <Typography sx={{ color: themeColors.subtleText, fontSize: 13.5 }}>
                Assign staff, set start time, pick how long cleaning took — end time is calculated automatically.
              </Typography>

              {!staffReady ? (
                <Box>
                  <Skeleton
                    variant="rounded"
                    height={48}
                    animation="wave"
                    sx={{
                      borderRadius: 1,
                      bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)',
                    }}
                  />
                  <Typography sx={{ mt: 0.75, fontSize: 12.5, color: themeColors.subtleText }}>
                    Loading housekeeping staff…
                  </Typography>
                </Box>
              ) : hasStaff ? (
                <FormControl fullWidth required error={Boolean(fieldErrors.staff)} size="small" sx={fieldSx}>
                  <InputLabel>Staff</InputLabel>
                  <Select
                    value={staffId === '' || staffId == null ? '' : staffId}
                    onChange={(event) => {
                      setStaffId(event.target.value);
                      setFieldErrors((prev) => ({ ...prev, staff: '' }));
                    }}
                    label="Staff"
                    MenuProps={drawerOverlays.selectMenuProps}
                  >
                    <MenuItem value="" sx={{ display: 'none' }} aria-hidden />
                    {staffList.map((staff) => (
                      <MenuItem key={staff.id} value={staff.id}>
                        {staff.full_name}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.staff ? <FormHelperText>{fieldErrors.staff}</FormHelperText> : null}
                </FormControl>
              ) : (
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 2,
                    alignItems: 'flex-start',
                    '& .MuiAlert-message': { width: '100%' },
                  }}
                >
                  <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                    Housekeeping staff required
                  </Typography>
                  <Typography sx={{ fontSize: 13.5, mb: 1.25 }}>
                    No staff members have been added for this hotel yet. Add at least one person
                    under Housekeeping → Staff, then return here to assign the room and mark it clean.
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      onClose?.();
                      navigate('/housekeeping/staff/');
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      backgroundColor: THEME_COLOR,
                      color: '#042f2e',
                      '&:hover': { backgroundColor: THEME_COLOR, opacity: 0.9 },
                    }}
                  >
                    Add housekeeping staff
                  </Button>
                </Alert>
              )}

              <Box sx={cleaningTimePanelSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <AccessTimeIcon sx={{ fontSize: 18, color: THEME_COLOR }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: themeColors.text }}>
                    Cleaning time
                  </Typography>
                </Box>

                <TimePicker
                  label="Start time"
                  value={startTime}
                  onChange={(value) => {
                    setStartTime(value);
                    setFieldErrors((prev) => ({ ...prev, start_time: '' }));
                  }}
                  ampm
                  timeSteps={{ minutes: 5 }}
                  views={['hours', 'minutes']}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      required: true,
                      error: Boolean(fieldErrors.start_time),
                      helperText: fieldErrors.start_time,
                      sx: {
                        ...fieldSx,
                        '& .MuiOutlinedInput-root': {
                          minHeight: 48,
                          backgroundColor: isDark ? themeColors.chipBg : '#fff',
                        },
                      },
                    },
                    openPickerButton: { size: 'small' },
                    ...drawerOverlays.datePickerSlotProps,
                  }}
                />

                <Box>
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: themeColors.subtleText,
                      mb: 0.75,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    Duration
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    value={durationMinutes}
                    onChange={(_, value) => {
                      if (value == null) return;
                      setDurationMinutes(value);
                      setFieldErrors((prev) => ({ ...prev, duration: '' }));
                    }}
                    sx={durationToggleSx}
                  >
                    {DURATION_OPTIONS.map((minutes) => (
                      <ToggleButton key={minutes} value={minutes}>
                        {minutes} min
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  {fieldErrors.duration ? (
                    <FormHelperText error sx={{ mt: 0.75, mx: 0 }}>
                      {fieldErrors.duration}
                    </FormHelperText>
                  ) : null}
                </Box>

                {startTime && endTime ? (
                  <Box
                    sx={{
                      px: 1.2,
                      py: 1,
                      borderRadius: 1.5,
                      bgcolor: isDark ? '#0f1218' : alpha('#000', 0.04),
                      border: `1px solid ${themeColors.surfaceBorderStrong}`,
                    }}
                  >
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: themeColors.text }}>
                      {startTime.format('h:mm A')}
                      {' → '}
                      {endTime.format('h:mm A')}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: themeColors.subtleText, mt: 0.25 }}>
                      {durationMinutes} minute{durationMinutes === 1 ? '' : 's'} total
                    </Typography>
                  </Box>
                ) : null}
              </Box>

              {!settingsReady && checklistItems.length === 0 ? (
                <CleaningChecklistSkeleton isDark={isDark} />
              ) : checklistEnabled && checklistItems.length > 0 ? (
                <HousekeepingCleaningChecklist
                  items={checklistItems}
                  checkedIds={checkedIds}
                  isDark={isDark}
                  error={fieldErrors.checklist}
                  disabled={!checklistEnabled}
                  status={derivedCleaningStatus}
                  onToggle={(itemId) => {
                    const next = new Set(checkedIds);
                    if (next.has(itemId)) next.delete(itemId);
                    else next.add(itemId);
                    setCheckedIds(next);
                    setFieldErrors((prev) => ({ ...prev, checklist: '' }));
                  }}
                />
              ) : null}

              <TextField
                fullWidth
                label="Remarks"
                multiline
                rows={3}
                size="small"
                value={remarks}
                onChange={(event) => setRemarks(event.target.value)}
                sx={{ '& .MuiInputBase-root': { minHeight: 'auto' } }}
              />

              {error ? <Alert severity="error">{error}</Alert> : null}
            </>
          ) : null}
        </Box>

        <Box
          sx={{
            p: 1.5,
            ...getHousekeepingDrawerFooterSx(isDark),
            display: 'flex',
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            onClick={onClose}
            sx={{
              borderColor: themeColors.surfaceBorder,
              color: isDark ? '#ddd' : '#666',
              '&:hover': {
                borderColor: THEME_COLOR,
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                color: THEME_COLOR,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handlePrimaryAction}
            disabled={loading || submitting || !cleaning || !staffReady || !hasStaff}
            sx={{
              backgroundColor: THEME_COLOR,
              color: '#000',
              fontWeight: 700,
              '&:hover': {
                backgroundColor: THEME_COLOR,
                opacity: 0.9,
              },
              '&.Mui-disabled': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                color: isDark ? '#777' : '#999',
              },
            }}
          >
            {submitting ? <CircularProgress size={18} color="inherit" /> : primaryActionLabel}
          </Button>
        </Box>
      </Drawer>
    </LocalizationProvider>
  );
}
